/* eslint-disable @typescript-eslint/no-explicit-any */
import IconArrow from '@assets/icons/big arrow right.svg?react';
import {
 Box,
 Chip,
 List,
 ListItem,
 ListItemText,
 Tooltip,
 Typography,
 useTheme,
} from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';

import customColors from '../../../utils/customColors';
import { isJson } from '../../../utils/dataTypes';
import { usePage } from '../../../contexts/usePageContext';
import { useTranslation } from 'react-i18next';
import UserAvatar from '../Header/UserAvatar';
import { getConfigStorageItem } from '@/hooks/useConfigStorage';

const limitColorMap = {
 red: customColors.lightRed,
 green: customColors.lightGreen,
 orange: customColors.lightOrange,
 yellow: customColors.lightYellow,
 blue: customColors.lightBlue,
};

const statusMap = {
 0: customColors.lightGreen,
 1: customColors.lightYellow,
 2: customColors.lightYellow,
 3: customColors.lightRed,
 4: customColors.lightBlue,
};

const xRenderCell = (
 params: GridRenderCellParams,
 name: string,
 type: string,
 customValue: string = '',
) => {
 const { pageName } = usePage();
 const { t } = useTranslation();
 const th = useTheme();
 const value =
  customValue && customValue !== 'undefined' ? customValue : params.value;

 if (params?.rowNode?.type === 'group') {
  return params?.formattedValue ? params.rowNode.groupingKey : value;
 }

 if (type === 'boolean') {
  return (
   <Typography
    className="badge-item"
    sx={{
     color: (theme) =>
      theme.palette.mode === 'dark'
       ? theme.palette.kemek.white // not actually white
       : theme.palette.kemek.darkestGray,
     backgroundColor: (statusMap as any)?.[value ? 0 : 3],
    }}
   >
    {value ? t('global.yes') : t('global.no')}
   </Typography>
  );
 }

 if ((!value && value !== 0) || value === 'null') return '';

 if (params.field === 'ProfileImageBase64' && params.row.ProfileImageBase64) {
  return (
   <Box
    sx={{
     width: '36px',
     height: '36px',
     backgroundImage: `url(data:image/png;base64,${params.row.ProfileImageBase64})`,
     backgroundSize: 'cover',
     backgroundPosition: 'center',
     backgroundRepeat: 'no-repeat',
     borderRadius: '50%',
     display: 'flex',
    }}
   />
  );
 }

 if (params.field === 'State') {
  return (
   <Typography
    className="badge-item"
    sx={{
     color: (theme) =>
      theme.palette.mode === 'dark'
       ? theme.palette.kemek.white // not actually white
       : theme.palette.kemek.darkestGray,
     backgroundColor: (statusMap as any)?.[params.value],
    }}
   >
    {value}
   </Typography>
  );
 }
 if (params.field === 'Cargo') {
  return (
   <Tooltip title={params.formattedValue === 1 ? 'From' : 'To'}>
    <Box
     sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      background: (theme) =>
       params.formattedValue === 1
        ? theme.palette.kemek.darkestGray
        : theme.palette.kemek.lightBlue,
      width: '24px',
      height: '24px',
     }}
    >
     <IconArrow
      title={value}
      style={{
       color:
        params.formattedValue === 1
         ? th.palette.kemek.white
         : th.palette.kemek.darkestGray,
       transform: params.formattedValue === 1 ? 'rotate(180deg)' : '',
      }}
     />
    </Box>
   </Tooltip>
  );
 }

 if (type === 'datetime') {
  if (value === null || value === undefined || value === '') {
   return '';
  }

  if (Array.isArray(value)) {
   if (!value.length) {
    return '';
   }

   const formattedDates = value
    .map((item: any) => {
     if (item === null || item === undefined || item === '') {
      return '';
     }

     const dateValue = dayjs(item);
     return dateValue.isValid() ? dateValue.format('YYYY-MM-DD HH:mm:ss') : '';
    })
    .filter(Boolean);

   return formattedDates.join(', ');
  }

  const dateValue = dayjs(value);
  return dateValue.isValid() ? dateValue.format('YYYY-MM-DD HH:mm:ss') : '';
 }

 // it is important to keep array check in this order
 const keysPriority = [
  'Name',
  'FullName',
  'Weight',
  'Number',
  'Time',
  'Client',
 ];
 if (type === 'array' || typeof value === 'object') {
  let arrayReturn = [];
  if (['ResultProducts', 'CardProducts'].includes(params.field)) {
   arrayReturn = value.map(
    (item: any) =>
     `${item.Product.Name}${item.Percentage ? ` (${item.Percentage}%)` : ''}`,
   );
  } else if (value[0] && typeof value[0] !== 'object') {
   arrayReturn = value;
  } else {
   if (value[0] === null) {
    arrayReturn = [];
   } else if (value[0] && keysPriority.some((key) => key in value[0])) {
    const keyFound = keysPriority.find((key) => key in value[0]) as string;
    arrayReturn = value.map((item: any) => item[keyFound as keyof typeof item]);
   } else {
    arrayReturn = Object.keys(value).length ? value : [];
   }
  }

  if (arrayReturn.length <= 1) {
   return <Typography>{arrayReturn.join(', ')}</Typography>;
  }

  return (
   <Tooltip
    title={
     <List sx={{ p: 0 }}>
      {arrayReturn.map((item: any, index: number) => (
       <ListItem key={`${item}-${index}`} disablePadding>
        <ListItemText primary={item} />
       </ListItem>
      ))}
     </List>
    }
   >
    <Chip
     //  color="primary"
     sx={{ fontWeight: 500 }}
     variant="outlined"
     label={`${arrayReturn[0]} +${arrayReturn.length - 1}`}
     size="small"
    />
   </Tooltip>
  );
 }

 if (['int32', 'int16', 'double', 'decimal'].includes(type)) {
  // rewrite fix
  const lsValue = getConfigStorageItem(pageName);

  const tempState = lsValue?.columns || [];

  const thisLimits = tempState?.limit?.[params.field] || [];
  let lastLimitIndex = -1;
  if (thisLimits.length) {
   thisLimits.forEach((item: any) => {
    if (item.limit === '<') {
     if (value < item.value) {
      lastLimitIndex = thisLimits.indexOf(item);
     }
    } else if (item.limit === '>') {
     if (value > item.value) {
      lastLimitIndex = thisLimits.indexOf(item);
     }
    }
   });
  }

  const selectedValue = value || params.value;
  const parts = selectedValue.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const formattedValue = parts.join('.');

  const hasLimit = lastLimitIndex !== -1;

  // && name !== 'Neto'  ???
  return !hasLimit || params.aggregation ? (
   formattedValue
  ) : (
   <Typography
    className="badge-item"
    sx={{
     color: (theme) =>
      theme.palette.mode === 'dark'
       ? theme.palette.kemek.white // not actually white
       : theme.palette.kemek.darkestGray,
     backgroundColor: (limitColorMap as any)?.[
      hasLimit ? thisLimits[lastLimitIndex].color : 'blue'
     ],
    }}
   >
    {formattedValue}
   </Typography>
  );
 }

 return value;
};

export default xRenderCell;

